import { useEffect, useState } from 'react'
import { data as remixData, useNavigate, useSearchParams } from 'react-router'
import { GeneralConfirmationScreen } from '#app/components/cotizacion/screens/general/GeneralConfirmationScreen'
import { GeneralFormScreen } from '#app/components/cotizacion/screens/general/GeneralFormScreen'
import { GeneralIntroScreen } from '#app/components/cotizacion/screens/general/GeneralIntroScreen'
import { Icon } from '#app/components/ui/icon'
import { Modal } from '#app/components/ui/modal'
import { ProgressBar } from '#app/components/ui/progress-bar'
import { sendCotizacionGeneralEmail } from '#app/utils/email.server.ts'
import { type Route } from './+types/cotizacion-general'

export const meta: Route.MetaFunction = () => [
	{ title: 'Cotización de Seguro - 100prevalor' },
	{
		name: 'description',
		content: 'Cotizá tu seguro con 100prevalor',
	},
]

export async function action({ request }: Route.ActionArgs) {
	const formData = await request.formData()
	const quoteDataString = formData.get('quoteData')

	if (typeof quoteDataString !== 'string') {
		return remixData(
			{ success: false, error: 'Datos inválidos' },
			{ status: 400 },
		)
	}

	const quoteData = JSON.parse(quoteDataString) as GeneralFormData

	try {
		await sendCotizacionGeneralEmail(quoteData)
		return remixData({ success: true })
	} catch (error) {
		console.error('Error sending email:', error)
		return remixData(
			{ success: false, error: 'Error al enviar el email' },
			{ status: 500 },
		)
	}
}

type GeneralFormData = {
	nombreApellido: string
	email: string
	telefono: string
	empresa?: string
	descripcion: string
	tipoSeguro?: string
}

export default function CotizacionGeneral() {
	const [searchParams, setSearchParams] = useSearchParams()
	const navigate = useNavigate()
	const step = parseInt(searchParams.get('step') || '1', 10)
	const tipoSeguro = searchParams.get('tipo') || undefined

	const [completedSteps, setCompletedSteps] = useState<Set<number>>(
		new Set([1]),
	)
	const [formData, setFormData] = useState<Partial<GeneralFormData>>({
		tipoSeguro,
	})

	const totalSteps = 3

	useEffect(() => {
		if (!completedSteps.has(step)) {
			const lastCompleted = Math.max(...Array.from(completedSteps))
			setSearchParams({ step: String(lastCompleted), ...(tipoSeguro ? { tipo: tipoSeguro } : {}) })
		}
	}, [step, completedSteps, setSearchParams, tipoSeguro])

	const getProgressPercentage = () => {
		if (step <= 1) return 0
		const progressMap: Record<number, number> = {
			2: 50,
			3: 100,
		}
		return progressMap[step] || 0
	}

	const currentProgress = getProgressPercentage()
	const showProgressBar = step >= 2

	const handleNext = async (stepData?: Partial<GeneralFormData>) => {
		if (stepData) {
			setFormData({ ...formData, ...stepData })
		}

		if (step === 2) {
			const finalData = { ...formData, ...stepData, tipoSeguro }

			try {
				const formDataToSend = new FormData()
				formDataToSend.append('quoteData', JSON.stringify(finalData))

				const response = await fetch('/cotizacion-general', {
					method: 'POST',
					body: formDataToSend,
				})

				if (response.ok) {
					console.log('✅ Email cotización general enviado exitosamente')
				} else {
					console.error('❌ Error al enviar email cotización general')
				}
			} catch (error) {
				console.error('❌ Error al enviar email cotización general:', error)
			}
		}

		if (step < totalSteps) {
			setCompletedSteps((prev) => new Set([...prev, step + 1]))
			const newParams: Record<string, string> = { step: String(step + 1) }
			if (tipoSeguro) newParams.tipo = tipoSeguro
			setSearchParams(newParams)
		}
	}

	const handleBack = () => {
		if (step > 1) {
			const newParams: Record<string, string> = { step: String(step - 1) }
			if (tipoSeguro) newParams.tipo = tipoSeguro
			setSearchParams(newParams)
		}
	}

	const handleClose = () => {
		void navigate('/seguros')
	}

	const handleRestart = () => {
		setFormData({ tipoSeguro })
		setCompletedSteps(new Set([1]))
		const newParams: Record<string, string> = { step: '1' }
		if (tipoSeguro) newParams.tipo = tipoSeguro
		setSearchParams(newParams)
	}

	const renderStep = () => {
		switch (step) {
			case 1:
				return (
					<GeneralIntroScreen
						onNext={() => handleNext()}
						tipoSeguro={tipoSeguro}
					/>
				)
			case 2:
				return (
					<GeneralFormScreen
						onNext={(data) => handleNext(data)}
						onBack={handleBack}
						data={formData}
						tipoSeguro={tipoSeguro}
					/>
				)
			case 3:
				return (
					<GeneralConfirmationScreen
						onBack={handleRestart}
						onClose={handleClose}
						data={formData as GeneralFormData}
					/>
				)
			default:
				return (
					<GeneralIntroScreen
						onNext={() => handleNext()}
						tipoSeguro={tipoSeguro}
					/>
				)
		}
	}

	return (
		<>
			<div className="fixed inset-0 z-50 h-screen bg-neutral-50 md:hidden">
				<div className="flex h-full flex-col">
					<div className="flex shrink-0 items-center justify-end p-4">
						<button
							onClick={handleClose}
							className="flex items-center justify-center rounded-full p-2 transition-colors hover:bg-neutral-200"
							aria-label="Cerrar"
						>
							<Icon name="cross-1" className="size-6 text-neutral-900" />
						</button>
					</div>

					<div className="flex min-h-0 flex-1 flex-col gap-6 px-6 pb-8">
						{showProgressBar && (
							<ProgressBar progress={currentProgress} icon="document-validation" />
						)}
						{renderStep()}
					</div>
				</div>
			</div>

			<div className="hidden md:block">
				<Modal
					isOpen={true}
					onClose={handleClose}
					showCloseButton={false}
					rounded={true}
					maxWidth={616}
				>
					<div className="relative flex h-[700px] w-[616px] flex-col rounded-3xl bg-white shadow-[0px_32px_48px_-12px_rgba(217,217,217,1)]">
						<div className="flex min-h-0 flex-1 flex-col gap-8 p-8">
							{showProgressBar && (
								<ProgressBar progress={currentProgress} icon="document-validation" />
							)}

							<div className="flex min-h-0 flex-1 flex-col">{renderStep()}</div>
						</div>
					</div>
				</Modal>
			</div>
		</>
	)
}
